import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { HandHeart, Gift, BookHeart, Newspaper, Quote, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { api, errMsg, formatDate } from "@/lib/api";
import { PageHero, SectionTitle, Empty } from "@/components/kit";
import { DynamicForm } from "@/components/forms/DynamicForm";
import { VOLUNTEER_FIELDS, DONATION_FIELDS } from "@/components/forms/configs";
import { NewsletterForm } from "@/components/forms/NewsletterForm";

function Block({ id, icon: Icon, title, intro, children }) {
  return (
    <section id={id} className="scroll-mt-28 py-14" data-testid={`section-${id}`}>
      <div className="container-x max-w-5xl">
        <div className="mb-8 flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-u text-white"><Icon className="h-6 w-6" aria-hidden="true" /></span>
          <SectionTitle title={title} intro={intro} />
        </div>
        {children}
      </div>
    </section>
  );
}

function Guestbook() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ name: "", city: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/guestbook").then((r) => setItems(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Veuillez accepter la politique de protection des données.");
    setLoading(true);
    try {
      await api.post("/guestbook", f);
      setSent(true);
      setF({ name: "", city: "", message: "", consent: false });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="grid gap-8 lg:grid-cols-2">
      <div className="space-y-4" data-testid="guestbook-list">
        {items && !items.length && <Empty testid="guestbook-empty">Soyez le premier à laisser un message de soutien.</Empty>}
        {(items || []).map((g) => (
          <figure key={g.id} className="card p-5">
            <Quote className="h-5 w-5 text-gold" aria-hidden="true" />
            <blockquote className="mt-2 whitespace-pre-line text-neutral-800">{g.message}</blockquote>
            <figcaption className="mt-3 text-sm font-semibold text-u">{g.name}{g.city && ` — ${g.city}`} <span className="font-normal text-neutral-500">· {formatDate(g.created_at)}</span></figcaption>
          </figure>
        ))}
      </div>
      <form onSubmit={submit} className="card space-y-4 p-6" data-testid="guestbook-form">
        {sent && <p className="rounded-md bg-u-soft p-3 text-sm" role="status" data-testid="guestbook-success">Merci ! Votre message sera publié après validation par un administrateur.</p>}
        <div><label className="label" htmlFor="gb-name">Nom *</label><input id="gb-name" required className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="guestbook-name" /></div>
        <div><label className="label" htmlFor="gb-city">Ville / pays</label><input id="gb-city" className="input" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="guestbook-city" /></div>
        <div><label className="label" htmlFor="gb-msg">Message de soutien *</label><textarea id="gb-msg" required rows={4} minLength={5} className="input" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="guestbook-message" /></div>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--u)]" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} data-testid="guestbook-consent" />J'accepte la publication de mon nom et de mon message après modération, et la politique de confidentialité.</label>
        <button className="btn-u" disabled={loading} data-testid="guestbook-submit">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Publier mon message</button>
      </form>
    </div>
  );
}

export default function Communication() {
  useSeo("Espace Communication", "Devenez bénévole, proposez un don en nature, laissez un message au livre d'or ou inscrivez-vous à la newsletter MA LEBA.");
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [hash]);
  const nav = [["#benevole", "Bénévolat"], ["#don", "Don en nature"], ["#livre-dor", "Livre d'or"], ["#newsletter", "Newsletter"]];
  return (
    <div data-testid="communication-page">
      <PageHero eyebrow="Dialogue" title="Espace Communication" intro="Engagez-vous à nos côtés, proposez votre aide et partagez vos messages de soutien." />
      <nav className="sticky top-16 z-30 border-b border-black/5 bg-white/90 backdrop-blur lg:top-[72px]" aria-label="Sections">
        <div className="container-x flex gap-2 overflow-x-auto py-3">
          {nav.map(([h, l]) => <a key={h} href={h} className="whitespace-nowrap rounded-full bg-u-soft px-4 py-1.5 font-display text-sm font-semibold uppercase text-u hover:bg-u hover:text-white" data-testid={`comm-nav-${h.slice(1)}`}>{l}</a>)}
        </div>
      </nav>
      <Block id="benevole" icon={HandHeart} title="Devenir bénévole" intro="Donnez de votre temps et de vos compétences.">
        <div className="card p-6 sm:p-10"><DynamicForm fields={VOLUNTEER_FIELDS} endpoint="/applications/benevole" testid="volunteer-form" submitLabel="Je deviens bénévole" /></div>
      </Block>
      <div className="pagne">
        <Block id="don" icon={Gift} title="Proposer un don en nature ou matériel" intro="Matériel, équipements, fournitures : chaque don compte.">
          <div className="card p-6 sm:p-10"><DynamicForm fields={DONATION_FIELDS} endpoint="/applications/don" testid="donation-form" submitLabel="Proposer mon don" /></div>
        </Block>
      </div>
      <Block id="livre-dor" icon={BookHeart} title="Livre d'or" intro="Laissez un message de soutien. Les messages sont publiés après validation.">
        <Guestbook />
      </Block>
      <div className="bg-white">
        <Block id="newsletter" icon={Newspaper} title="Newsletter" intro="Recevez les nouvelles de l'ONG, de l'Association, ou des deux.">
          <div className="card max-w-lg p-6"><NewsletterForm /></div>
          <Link to="/lettres" className="btn-outline mt-6 text-u" data-testid="communication-letters-link">Lire les lettres d'information</Link>
        </Block>
      </div>
    </div>
  );
}
